/**
 * Theme Picker Component
 *
 * Dropdown picker for selecting app theme.
 * Shows available themes with visual preview.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { ThemeId } from '../../theme/types';

interface ThemeOption {
  id: ThemeId;
  nameKey: string;
  descriptionKey: string;
}

const THEME_OPTIONS: ThemeOption[] = [
  {
    id: 'bauhaus',
    nameKey: 'settings.themeBauhaus',
    descriptionKey: 'settings.themeBauhausDesc',
  },
  {
    id: 'kinetic',
    nameKey: 'settings.themeKinetic',
    descriptionKey: 'settings.themeKineticDesc',
  },
];

export function ThemePicker() {
  const { theme, themeId, setTheme } = useTheme();
  const { t } = useTranslation();

  const handleThemeChange = async (newThemeId: ThemeId) => {
    await setTheme(newThemeId);
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
        {t('settings.appTheme')}
      </Text>

      <ScrollView
        style={styles.scrollContainer}
        horizontal={false}
        showsVerticalScrollIndicator={false}
      >
        {THEME_OPTIONS.map((option) => (
          <TouchableOpacity
            key={option.id}
            style={[
              styles.option,
              {
                backgroundColor: theme.colors.surface,
                borderColor: themeId === option.id ? theme.colors.primary : theme.colors.border,
                borderWidth: themeId === option.id ? 3 : 2,
              },
            ]}
            onPress={() => handleThemeChange(option.id)}
            activeOpacity={0.7}
          >
            <View style={styles.optionContent}>
              <Text
                style={[
                  styles.optionName,
                  {
                    color: theme.colors.text,
                    fontWeight:
                      themeId === option.id
                        ? theme.typography.fontWeightBold
                        : theme.typography.fontWeightMedium,
                  },
                ]}
              >
                {t(option.nameKey)}
              </Text>
              <Text style={[styles.optionDescription, { color: theme.colors.textSecondary }]}>
                {t(option.descriptionKey)}
              </Text>
            </View>

            {themeId === option.id && (
              <View style={[styles.checkmarkContainer, { backgroundColor: theme.colors.primary }]}>
                <Text style={styles.checkmark}>✓</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
  },
  scrollContainer: {
    maxHeight: 200,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    marginBottom: 8,
  },
  optionContent: {
    flex: 1,
  },
  optionName: {
    fontSize: 16,
    marginBottom: 4,
  },
  optionDescription: {
    fontSize: 14,
  },
  checkmarkContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
});
